import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { extractOidcCallbackToken } from '../utils/oidcCallback.js';
import { isElectron } from '../utils/electron';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('etch.token'));
  const [loading, setLoading] = useState(true);

  // Capture the OIDC callback token before the session-restore effect runs so
  // it lands in localStorage immediately, then strip it from the URL so a
  // refresh or copy-paste of the URL doesn't re-process/re-expose it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const result = extractOidcCallbackToken(window.location.hash);
    if (result) {
      localStorage.setItem('etch.token', result.token);
      setToken(result.token);
      const cleaned = `${window.location.pathname}${window.location.search}${result.remainingHash ? '#' + result.remainingHash : ''}`;
      window.history.replaceState({}, '', cleaned);
    }
  }, []);

  useEffect(() => {
    async function restoreSession() {
      const stored = localStorage.getItem('etch.token');
      if (!stored) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch('/api/auth/me', {
          headers: { 'Authorization': `Bearer ${stored}` },
        });
        if (res.ok) {
          const data = await res.json();
          setUser(data.user || data);
          setToken(stored);
        } else {
          localStorage.removeItem('etch.token');
          setToken(null);
        }
      } catch {
        localStorage.removeItem('etch.token');
        setToken(null);
      } finally {
        setLoading(false);
      }
    }
    restoreSession();
  }, []);

  // Remember which tenant this browser last signed into. The login screen runs
  // before any session exists, so this is the only way a returning visitor sees
  // their own tenant's identity instead of a neutral one. Deliberately survives
  // logout — the next person at this machine is overwhelmingly the same person.
  useEffect(() => {
    const slug = user?.tenant_slug;
    if (!slug) return;
    try {
      localStorage.setItem('etch.tenant', slug);
    } catch {
      // Blocked storage just means the neutral screen next time.
    }
  }, [user]);

  const login = useCallback(async (username, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Login failed' }));
      throw new Error(err.error || err.message || 'Login failed');
    }
    const data = await res.json();
    localStorage.setItem('etch.token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const register = useCallback(async (username, email, password) => {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Registration failed' }));
      throw new Error(err.error || err.message || 'Registration failed');
    }
    const data = await res.json();
    localStorage.setItem('etch.token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const setSession = useCallback((token, user) => {
    localStorage.setItem('etch.token', token);
    setToken(token);
    setUser(user);
  }, []);

  const logout = useCallback(async () => {
    const stored = localStorage.getItem('etch.token');
    localStorage.removeItem('etch.token');
    setToken(null);
    setUser(null);

    // The platform keeps its own SSO session. Without visiting its logout URL,
    // "Mit nyxCore anmelden" signs the user straight back in.
    let endSessionUrl = null;
    try {
      const res = await fetch('/api/auth/oidc/logout', {
        method: 'POST',
        headers: stored ? { Authorization: `Bearer ${stored}` } : {},
      });
      if (res.ok) endSessionUrl = (await res.json()).endSessionUrl;
    } catch {
      // Offline or OIDC not mounted: the local logout above still holds.
    }
    if (endSessionUrl && !isElectron) window.location.assign(endSessionUrl);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, setSession, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
